// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { PATHWAYS_PAGES, PATHWAYS_SECTIONS } from "~shared-pathways";

import { TenantConfig } from "../core/models/types";
import { METRIC_TYPE_LABELS, METRIC_TYPES } from "../core/PageVitals/types";
import enabledTableColumns from "../core/utils/enabledTableColumns";
import { INSIGHTS_PAGES } from "../core/views";
import * as pathways from "../RootStore/TenantStore/pathwaysTenants";
import UsIdEmploymentVerificationTaskV2 from "../WorkflowsStore/Task/US_ID/UsIdEmploymentVerificationTaskV2";
import UsIdFaceToFaceContactTask from "../WorkflowsStore/Task/US_ID/UsIdFaceToFaceContactTask";
import UsIdHomeVisitTaskV2 from "../WorkflowsStore/Task/US_ID/UsIdHomeVisitTaskV2";
import UsIdLsirAssessmentTask from "../WorkflowsStore/Task/US_ID/UsIdLsirAssessmentTask";
import UsIdStableAssessmentTask from "../WorkflowsStore/Task/US_ID/UsIdStableAssessmentTask";
import { filterByUserDistrict } from "../WorkflowsStore/utils";

const US_ID_CONFIG = {
  name: "Idaho",
  stateCode: "ID",
  domain: "idoc.idaho.gov",
  DOCName: "IDOC",
  availableStateCodes: [pathways.US_ID],
  enableUserRestrictions: false,
  workflowsMethodologyUrl:
    "https://docs.google.com/document/d/e/2PACX-1vT4TRUUKqjyFhpZ_-9vCTT_sWYeXbteeg4Lyp4EK2UdPuMLVqC0e0cU2zrmoAzQ5XjAanzw0lFCA-jP/pub",
  workflowsTasksConfig: {
    collection: "usIdSupervisionTasks",
    methodologyUrl:
      "https://docs.google.com/document/d/e/2PACX-1vRN_KixvGRM-PlVlgyqThRaehhmXs-1j3iNJBOv9XWW00caonEqd4t0czI1ohN6VjyJWc16AqCr7rUg/pub",
    categories: ["ALL_TASKS", "OVERDUE", "DUE_THIS_MONTH", "DUE_LATER"],
    columns: [
      "name",
      "id",
      "task",
      "dueDate",
      "lastContactDate",
      "frequency",
      "supervisionLevel",
      "caseType",
      "tasksDue",
    ],
    pageDescriptionMarkdown:
      "The clients below might have upcoming requirements this month. Hiding a below task will not change an officer's timeliness percentage in the Operations metrics.",
    tasks: {
      usIdFaceToFaceContact: {
        constructor: UsIdFaceToFaceContactTask,
        snoozeForOptionsInDays: [7, 30, 90],
      },
      usIdHomeVisit: {
        constructor: UsIdHomeVisitTaskV2,
        snoozeForOptionsInDays: [7, 30, 90],
      },
      usIdEmploymentVerification: {
        constructor: UsIdEmploymentVerificationTaskV2,
        snoozeForOptionsInDays: [7, 30, 90],
      },
      usIdLsirAssessment: {
        constructor: UsIdLsirAssessmentTask,
        snoozeForOptionsInDays: [7, 30, 90],
      },
      usIdStableAssessment: {
        constructor: UsIdStableAssessmentTask,
        snoozeForOptionsInDays: [7, 30, 90],
      },
    },
    filters: [
      {
        title: "Supervision Level",
        field: "supervisionLevel",
        type: "person",
        options: [
          {
            value: "Low",
          },
          {
            value: "Moderate",
          },
          {
            value: "High",
          },
          {
            label: "Expanded CRC",
            value: "Expanded Crc",
          },
        ],
      },
      {
        title: "Task Type",
        field: "type",
        type: "task",
        options: [
          {
            value: "usIdFaceToFaceContact",
            label: "Face-to-Face Contact",
            shortLabel: "Contact",
          },
          {
            value: "usIdHomeVisit",
            label: "Home/Field Contact",
          },
          {
            value: "usIdLsirAssessment",
            label: "LSIR Assessment",
            shortLabel: "LSIR Assessment",
          },
          {
            value: "usIdStableAssessment",
            label: "STABLE Assessment",
            shortLabel: "STABLE Assessment",
          },
          {
            value: "usIdEmploymentVerification",
            label: "Employment Verification",
            shortLabel: "Employment",
          },
        ],
      },
    ],
  },
  workflowsSupportedSystems: ["SUPERVISION", "INCARCERATION"],
  workflowsSystemConfigs: {
    INCARCERATION: {
      search: [
        {
          searchType: "US_ID_CRC_FACILITY",
          searchField: ["metadata", "crcFacilities"],
          searchTitle: "facility",
          searchOp: "array-contains-any",
          onlySurfaceEligible: true,
          restrictedToFeatureVariant: "usIdCRCFacilitySearch",
        },
        {
          searchType: "INCARCERATION_OFFICER",
          searchField: ["officerId"],
          searchTitle: "case manager",
          restrictedToFeatureVariant: "usIdCaseManagerSearch",
        },
      ],
    },
    SUPERVISION: {
      search: [
        {
          searchType: "DISTRICT",
          searchField: ["district"],
          searchTitle: "district",
          restrictedToFeatureVariant: "usIdDistrictSearch",
        },
        {
          searchType: "OFFICER",
          searchField: ["officerId"],
          searchTitle: "supervision officer",
        },
      ],
      staffFilterFn: filterByUserDistrict,
    },
  },
  navigation: {
    // The order of pages here determines where the user will land first when navigating from `/`
    insights: [INSIGHTS_PAGES.supervision],
    workflows: ["home", "clients", "tasks", "residents"],
    operations: [],
    parole: ["docket"],
    system: [
      PATHWAYS_PAGES.libertyToPrison,
      PATHWAYS_PAGES.prison,
      PATHWAYS_PAGES.prisonToSupervision,
      PATHWAYS_PAGES.supervision,
      PATHWAYS_PAGES.supervisionToPrison,
      PATHWAYS_PAGES.supervisionToLiberty,
    ],
    libertyToPrison: [
      PATHWAYS_SECTIONS.countOverTime,
      PATHWAYS_SECTIONS.countByLocation,
      PATHWAYS_SECTIONS.countByPriorLengthOfIncarceration,
      PATHWAYS_SECTIONS.countByGender,
      PATHWAYS_SECTIONS.countByAgeGroup,
      PATHWAYS_SECTIONS.countByRace,
    ],
    prison: [
      PATHWAYS_SECTIONS.countOverTime,
      PATHWAYS_SECTIONS.countByLocation,
      PATHWAYS_SECTIONS.countByRace,
      PATHWAYS_SECTIONS.personLevelDetail,
    ],
    prisonToSupervision: [
      PATHWAYS_SECTIONS.countOverTime,
      PATHWAYS_SECTIONS.countByLocation,
      PATHWAYS_SECTIONS.countByAgeGroup,
      PATHWAYS_SECTIONS.countByRace,
      PATHWAYS_SECTIONS.personLevelDetail,
    ],
    supervision: [
      PATHWAYS_SECTIONS.countOverTime,
      PATHWAYS_SECTIONS.countByLocation,
      PATHWAYS_SECTIONS.countBySupervisionLevel,
      PATHWAYS_SECTIONS.countByRace,
    ],
    supervisionToPrison: [
      PATHWAYS_SECTIONS.countOverTime,
      PATHWAYS_SECTIONS.countByLengthOfStay,
      PATHWAYS_SECTIONS.countByLocation,
      // TODO #3088 Re-enable officer chart when data is ready
      // PATHWAYS_SECTIONS.countByOfficer,
      PATHWAYS_SECTIONS.countBySupervisionLevel,
      PATHWAYS_SECTIONS.countByGender,
      PATHWAYS_SECTIONS.countByRace,
    ],
    supervisionToLiberty: [
      PATHWAYS_SECTIONS.countOverTime,
      PATHWAYS_SECTIONS.countByLengthOfStay,
      PATHWAYS_SECTIONS.countByLocation,
      PATHWAYS_SECTIONS.countByGender,
      PATHWAYS_SECTIONS.countByAgeGroup,
      PATHWAYS_SECTIONS.countByRace,
    ],
    psi: [],
    methodology: ["system", "operations"],
  },
  paroleConfig: {
    docketConfig: {
      columns: [
        { header: "Name", field: "individualName", sortable: true },
        { header: "IDOC ID", field: "displayId", format: "docId" },
        {
          header: "Hearing Month",
          field: "hearingDate",
          format: "month",
          sortable: true,
        },
        { header: "Hearing Type", field: "hearingType" },
        { header: "Facility", field: "facility" },
      ],
      windowDaysBefore: 7,
      windowDaysAfter: 30,
    },
    conductHistoryConfig: {
      classificationColors: {
        "Class A": "BROWN",
        "Class B": "GOLD",
        "Class C": "TEAL",
      },
      visibleYears: 3,
    },
    riskAssessmentConfig: {
      tools: ["LSIR", "VRAG", "STATIC_99", "STABLE"],
      aggregateView: {
        label: "All",
        tools: ["LSIR", "VRAG", "STATIC_99", "STABLE"],
      },
    },
  },
  tableColumns: enabledTableColumns[pathways.US_ID],
  vitalsMetrics: [
    {
      name: METRIC_TYPE_LABELS.OVERALL,
      id: METRIC_TYPES.OVERALL,
      description: "Average timeliness across all metrics",
      accessor: "overall",
    },
    {
      name: METRIC_TYPE_LABELS.RISK_ASSESSMENT,
      id: METRIC_TYPES.RISK_ASSESSMENT,
      description: `of clients have an up-to-date risk assessment, according to IDOC policy.`,
      accessor: "timelyRiskAssessment",
    },
    {
      name: METRIC_TYPE_LABELS.CONTACT,
      id: METRIC_TYPES.CONTACT_DUE_DATE_BASED,
      description: `of clients have up-to-date contacts, according to IDOC policy`,
      accessor: "timelyContactDueDateBased",
    },
    {
      name: METRIC_TYPE_LABELS.DOWNGRADE,
      id: METRIC_TYPES.DOWNGRADE,
      description: `of clients are at a supervision level that is the same or below their risk level.`,
      accessor: "timelyDowngrade",
    },
  ],
  releaseDateCopyOverride: "Full Term Release Date",
  featureVariants: {
    outcomesModule: {},
  },
} satisfies TenantConfig<"US_ID">;

export default US_ID_CONFIG;
